/*
 * Boston Proper × product card analytics tracker.
 *
 * One global script — uses event delegation so every `.bp-card`
 * surface (PLP, search, carousel, our-most-loved, styled-for-you,
 * article-shoppable, brands directory) gets select_item +
 * add_to_cart tracking without per-section JS edits.
 *
 * Listens on:
 *   click on `.bp-card__media-link`           → select_item
 *   click on `[data-bp-card-qv-trigger]`      → select_item (writes
 *                                                attribution context,
 *                                                add_to_cart fires
 *                                                later from the drawer
 *                                                or directly).
 *
 * Reads from:
 *   article.bp-card[data-bp-track-product]    → JSON product payload
 *                                                (set in bp-product-card
 *                                                .liquid)
 *   ancestor[data-bp-track-list-name]         → optional list name
 *                                                (set per section —
 *                                                e.g., "Site Browse",
 *                                                "Search Results",
 *                                                "Product Carousel")
 *
 * select_item also stores list-context to localStorage so the
 * downstream add_to_cart event (which may fire from the drawer or
 * Shopify's checkout pixel) can carry item_list_name through.
 */

(function () {
  'use strict';

  if (window.__bpTrackCardsWired) return;
  window.__bpTrackCardsWired = true;

  function readProduct(card) {
    var raw = card && card.getAttribute && card.getAttribute('data-bp-track-product');
    if (!raw) return null;
    try { return JSON.parse(raw); }
    catch (_) { return null; }
  }

  function readListName(card) {
    var node = card;
    while (node && node !== document.body) {
      var name = node.getAttribute && node.getAttribute('data-bp-track-list-name');
      if (name) return name;
      node = node.parentElement;
    }
    // URL-pattern fallback when the section hasn't tagged itself.
    var p = window.location.pathname;
    if (p.indexOf('/collections/') === 0) return 'Site Browse';
    if (p.indexOf('/search')       === 0) return 'Search Results';
    if (p === '/' || p === '')            return 'Homepage';
    return 'Other';
  }

  function readIndex(card) {
    // Find this card's position within its closest list container.
    var list = card.closest('[data-bp-track-list-name], .bp-pc__track, .bp-yall__rail, ul, .bp-coll__grid, .bp-grid__inner');
    if (!list) return null;
    var siblings = list.querySelectorAll('.bp-card');
    for (var i = 0; i < siblings.length; i += 1) {
      if (siblings[i] === card) return i; // GA4 index is 0-based (was i + 1)
    }
    return null;
  }

  function shapeProductForTrack(p) {
    // Normalize the JSON shape into what bpTrack._itemFromProduct expects.
    return {
      id:           p.id,
      title:        p.title,
      vendor:       p.vendor,
      handle:       p.handle,
      url:          p.url,
      sku:          p.sku,        // item-level SKU → item_id / item_parent_id
      product_type: p.product_type,
      metafields: {
        BP_ProductAttributes: {
          bp_department: p.category || ''
        }
      },
      selected_variant: p.selected_variant
    };
  }

  // Merge Storefront-sourced track data (variant id / department / inventory)
  // into a shaped product so its GA4 item carries item_shopify_variant /
  // item_category / item_stock_qty. Shared by select_item + Quickview.
  function applyTrackData(product, d) {
    if (!d) return;
    var sv = product.selected_variant || (product.selected_variant = {});
    if (d.variantId) sv.id = d.variantId;
    if (d.quantityAvailable != null) sv.inventory_quantity = d.quantityAvailable;
    if (d.color) sv.option1 = d.color;   // → item_category4 (Color)
    if (d.size) sv.option2 = d.size;     // → item_category5 (Size)
    if (d.department) {
      product.metafields = product.metafields || {};
      product.metafields.BP_ProductAttributes = product.metafields.BP_ProductAttributes || {};
      product.metafields.BP_ProductAttributes.bp_department = d.department;
    }
  }

  function fireSelect(card) {
    if (!window.bpTrack || !window.bpTrack.select_item) return;
    var raw = readProduct(card);
    if (!raw) return;
    var product = shapeProductForTrack(raw);
    // Enrich SYNCHRONOUSLY from the view_item_list cache (populated when the
    // grid's view_item_list fired its batched Storefront lookup). A card click
    // navigates to the PDP, so an async fetch here would race the navigation
    // and lose the event — the cache avoids that. Cache miss (card clicked
    // before the grid enriched) → those fields stay hidden per "hide if empty".
    var cache = window.__bpTrackData;
    if (cache && raw.handle && cache[raw.handle]) applyTrackData(product, cache[raw.handle]);
    var listName = readListName(card);
    var index = readIndex(card);
    // PLP browse cards: location_id (+ item_list_id) = "Product Listing" (the
    // surface) while item_list_name stays "Site Browse" (GA4 QA #2 — do NOT
    // relabel item_list_name to "Product Listing"). Other surfaces (carousels,
    // search) keep their own location.
    var locationId = listName === 'Site Browse' ? 'Product Listing' : null;
    // Search-experience clicks (search drawer cards, list "Search Results"):
    // location_id/item_list_id/event_location = "Search Experience" and carry
    // the typed term (GA4 QA #2 — there's no search landing page, so this is
    // the only search signal).
    var searchTerm;
    if (listName === 'Search Results') {
      locationId = 'Search Experience';
      var si = document.querySelector('.bp-sd__input, #bp-sd-input');
      searchTerm = (si && si.value) ? si.value.trim() : '';
    }
    window.bpTrack.select_item(product, listName, index, locationId, searchTerm);
  }

  // PLP Quickview → fire view_item with event_location "Quickview" and flag
  // the session so the add_to_cart that may follow (from the drawer) is
  // attributed to "Quickview" too (read by bp-track-cart initialAddLocation).
  // Only when the trigger opens the drawer — a "pdp" destination navigates
  // to the product page, which fires its own "Product Detail" view_item.
  function fireQuickview(card) {
    var dest = card.getAttribute('data-bp-card-qv-dest');
    if (dest && dest !== 'drawer') return;
    if (!window.bpTrack || !window.bpTrack.view_item) return;
    var raw = readProduct(card);
    if (!raw) return;
    var product = shapeProductForTrack(raw);
    // "Cart Quickview" when the quickview is opened from the cart (flyout / cart
    // page complete-the-look upsell), else "Quickview" (GA4 QA #2). The swatch
    // re-fire below reads window.__bpQuickviewLoc so a colour swap in the modal
    // keeps the same surface.
    var loc = card.closest('.bp-cart-drawer, .bp-cart-page, [data-bp-cart-dialog], [data-bp-ctl], [data-bp-cart-line], [data-bp-bundle-upsell]')
      ? 'Cart Quickview' : 'Quickview';
    window.__bpQuickviewLoc = loc;
    window.__bpQuickviewActive = true;
    // Safety: clear the flag if the drawer never resolves to an add.
    clearTimeout(window.__bpQuickviewTimer);
    window.__bpQuickviewTimer = setTimeout(function () { window.__bpQuickviewActive = false; }, 30000);

    var fire = function () {
      window.bpTrack.view_item(product.selected_variant, product, loc);
    };
    // The BR card payload is lean — no variant id, department, or inventory —
    // so item_shopify_variant / item_category / item_stock_qty come back empty.
    // Reuse the view_item_list cache if present; else do ONE Storefront lookup
    // by handle. Fire immediately with what we have if it fails.
    var cache = window.__bpTrackData;
    if (cache && raw.handle && cache[raw.handle]) { applyTrackData(product, cache[raw.handle]); fire(); return; }
    var enrich = window.bpColorSiblings && window.bpColorSiblings.fetchTrackData;
    if (enrich && raw.handle) {
      enrich(raw.handle).then(function (map) {
        var d = map && map[raw.handle];
        if (d) {
          window.__bpTrackData = window.__bpTrackData || {};
          window.__bpTrackData[raw.handle] = d;
          applyTrackData(product, d);
        }
      }).catch(function () {}).then(fire);
    } else {
      fire();
    }
  }

  // Fetch a product by handle and fire view_item for the given surface. Shared
  // by the quickview swatch re-fire and the cart-quickview open helper — both
  // only have a product URL/handle (no view_item_list cache guarantee), so a
  // single /products/<handle>.js lookup builds the item (incl. colour/size via
  // option1/option2 and sale price via compare_at_price).
  function fireViewItemFromHandle(handle, loc) {
    if (!handle || !window.bpTrack || !window.bpTrack.view_item) return;
    window.fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (pj) {
        if (!pj) return;
        var v = (pj.variants && pj.variants[0]) || {};
        var product = {
          id: pj.id, title: pj.title, url: pj.url, handle: pj.handle,
          vendor: pj.vendor, product_type: pj.type,
          selected_variant: {
            id: v.id, sku: v.sku, price: v.price,
            compare_at_price: v.compare_at_price,
            option1: v.option1, option2: v.option2,
            available: v.available
          }
        };
        window.bpTrack.view_item(product.selected_variant, product, loc);
      })
      .catch(function () {});
  }

  // Cart complete-the-look / bundle-upsell open their own bespoke quick-add
  // trigger (a dynamic <quick-add-component> + window.bpOpenProductDrawer, NOT a
  // .bp-card with data-bp-card-qv-trigger), so fireQuickview above never runs
  // for them. Those cart snippets call this helper when they open the drawer so
  // the Cart Quickview view_item fires (GA4 QA #2). Sets __bpQuickviewLoc so an
  // in-drawer colour-swatch swap re-fires with the same "Cart Quickview" surface.
  window.bpTrackCartQuickview = function (productUrl) {
    if (!productUrl) return;
    var m = String(productUrl).split('?')[0].match(/\/products\/([^/?#]+)/);
    if (!m) return;
    window.__bpQuickviewLoc = 'Cart Quickview';
    window.__bpQuickviewActive = true;
    clearTimeout(window.__bpQuickviewTimer);
    window.__bpQuickviewTimer = setTimeout(function () { window.__bpQuickviewActive = false; }, 30000);
    fireViewItemFromHandle(m[1], 'Cart Quickview');
  };

  // Quickview colour-swatch → re-fire view_item (GA4 QA #2). Selecting a colour
  // swatch INSIDE the quickview modal swaps the sibling product in place but
  // didn't re-fire view_item. Catch swatch clicks scoped to the modal and
  // re-fire with the SAME quickview surface (Quickview / Cart Quickview via
  // __bpQuickviewLoc). PDP swatches are handled by bp-track-pdp, so scoping to
  // the modal avoids a double fire there.
  document.addEventListener('click', function (event) {
    var sw = event.target.closest && event.target.closest('a[data-bp-swatch][href]');
    if (!sw) return;
    var modal = sw.closest('#quick-add-modal, .quick-add-modal, #quick-add-dialog, .bp-qd, [data-bp-quickview]');
    if (!modal) return;
    var href = sw.getAttribute('href') || '';
    var m = href.split('?')[0].match(/\/products\/([^/?#]+)/);
    if (!m) return;
    fireViewItemFromHandle(m[1], window.__bpQuickviewLoc || 'Quickview');
  }, true);

  // Clear Quickview attribution when the quick-add drawer closes without an
  // add. `close` doesn't bubble, but the capture phase still reaches here.
  document.addEventListener('close', function (event) {
    var t = event.target;
    if (t && t.id === 'quick-add-modal') window.__bpQuickviewActive = false;
  }, true);

  // Single delegated click handler — capture phase so we run before
  // the navigation/drawer-open logic actually starts the network fetch.
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var card = target.closest('.bp-card');
    if (!card) return;

    var mediaLink = target.closest('.bp-card__media-link');
    var bagBtn    = target.closest('[data-bp-card-qv-trigger]');
    var titleLink = target.closest('.bp-card__title a, .bp-card__body a, .bp-card__price');

    if (mediaLink || bagBtn || titleLink) {
      fireSelect(card);
    }
    // The bag / quick-view trigger also opens Quickview → view_item.
    if (bagBtn) {
      fireQuickview(card);
    }
  }, true);

  // Prime the enrichment cache on hover so a select_item click has
  // item_shopify_variant / item_category / item_stock_qty ready SYNCHRONOUSLY.
  // A card click navigates to the PDP, so we can't fetch at click time — and
  // the grid's view_item_list batch-cache may not have landed yet when a
  // shopper clicks quickly, or the card was lazy-loaded after that batch fired.
  // One tiny Storefront lookup per hovered handle (deduped) closes that race.
  var _primed = {};
  document.addEventListener('mouseover', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;
    var card = t.closest('.bp-card');
    if (!card) return;
    var raw = readProduct(card);
    if (!raw || !raw.handle) return;
    if ((window.__bpTrackData && window.__bpTrackData[raw.handle]) || _primed[raw.handle]) return;
    var enrich = window.bpColorSiblings && window.bpColorSiblings.fetchTrackData;
    if (!enrich) return;
    _primed[raw.handle] = true;
    enrich(raw.handle).then(function (map) {
      var d = map && map[raw.handle];
      if (d) {
        window.__bpTrackData = window.__bpTrackData || {};
        window.__bpTrackData[raw.handle] = d;
      }
    }).catch(function () { delete _primed[raw.handle]; });
  }, true);
})();
